import { Grid } from '@afterpaytouch/core'
import React, { forwardRef, ReactElement, ReactNode, useEffect, useImperativeHandle, useRef } from 'react'
import { DeepPartial, FormProvider, Path, ResolverResult, useForm, UseFormProps } from 'react-hook-form'
import {
  AddressField,
  CardField,
  CheckboxField,
  DateField,
  DateOfBirthField,
  DropdownField,
  NumberField,
  PasswordField,
  PhoneField,
  TextField,
  MoneyField,
} from './fields'
import { Field, FieldType, Schema } from './types'
import { compareObjects } from './utils/object'

export interface FormBuilderProps<T> {
  onSubmit?: (data: any) => void
  onError?: (data: any) => void
  onStateChange?: (valid: boolean, errors: any) => void
  onChange?: (data: any) => void
  onFocus?: (data: any) => void
  // @ts-expect-error: OPERATION BLEED STOPPER
  schema: Schema<T>
  // @ts-expect-error: OPERATION BLEED STOPPER
  formOptions?: UseFormProps<T>
  testNameSpace?: string
  disabled?: boolean
}

export interface FormBuilderRef {
  triggerValidation: () => Promise<boolean>
}

function FormBuilder<T>(
  {
    onSubmit = () => {},
    onError = () => {},
    onStateChange = () => {},
    onChange = () => {},
    onFocus = () => {},
    schema,
    formOptions = {
      mode: 'onBlur',
    },
    testNameSpace = '',
    disabled = false,
  }: FormBuilderProps<T>,
  ref: React.Ref<FormBuilderRef>
): ReactElement {
  // @ts-expect-error: OPERATION BLEED STOPPER
  const form = useForm<T>(formOptions)
  const {
    watch,
    trigger,
    formState: { isValid, errors },
  } = form
  const transformedSchema = Object.entries<Field>(schema)
  const componentQueue = useRef<Array<[string, Field]>>([])
  const prevValues = useRef<ResolverResult<DeepPartial<T>>>()

  useImperativeHandle(ref, () => ({
    triggerValidation: trigger,
  }))

  const renderSingleField = ([name, props]: [string, Field]): ReactNode => {
    let Component: any = null
    const { type, show = true, inline, ...otherProps } = props
    if (typeof show === 'boolean' && !show) {
      return null
    }

    switch (props.type) {
      case FieldType.Component:
        return (
          <Grid.Item key={name}>
            <props.Component />
          </Grid.Item>
        )
      case FieldType.Dropdown:
        Component = DropdownField
        break
      case FieldType.DateOfBirth:
        Component = DateOfBirthField
        break
      case FieldType.Address:
        Component = AddressField
        break
      case FieldType.Date:
        Component = DateField
        break
      case FieldType.Card:
        Component = CardField
        break
      case FieldType.Phone:
        Component = PhoneField
        break
      case FieldType.Checkbox:
        Component = CheckboxField
        break
      case FieldType.Password:
        Component = PasswordField
        break
      case FieldType.Money:
        Component = MoneyField
        break
      case FieldType.Number:
        Component = NumberField
        break
      default:
        Component = TextField
    }

    const formComponentProps = {
      ...(disabled && { disabled }),
    }
    const componentProps = { ...otherProps, props: { ...otherProps.props, ...formComponentProps } }
    const isFixedItem = typeof inline === 'number' && inline > 0
    const componentTestNameSpace = [testNameSpace, name].join('-')

    if (isFixedItem) {
      // TODO: discuss with designer to come out with a better grid system.
      // TODO: refactor <Grid.FixedItem> so that we don't use inline styles
      return (
        <Grid.FixedItem width={inline} key={name}>
          <Component name={name} testNameSpace={componentTestNameSpace} {...componentProps} show={show} validationMode={formOptions.mode} />
        </Grid.FixedItem>
      )
    } else {
      return (
        <Grid.Item key={name}>
          <Component name={name} testNameSpace={componentTestNameSpace} {...componentProps} show={show} validationMode={formOptions.mode} />
        </Grid.Item>
      )
    }
  }

  const renderField = (kv: [string, Field], idx: number): ReactNode => {
    const [name, props] = kv
    const { type, show = true, inline = false } = props

    if (typeof show === 'boolean' && !show) {
      if (typeof form.getValues(name as Path<T>) !== 'undefined') {
        form.control.unregister(name as Path<T>)
      }
      return null
    }

    const isInlineField = (typeof inline === 'number' && inline > 0) || (typeof inline === 'boolean' && inline)

    if (isInlineField && type !== FieldType.Component) {
      componentQueue.current.push(kv)
      return null
    } else if (componentQueue.current.length > 0) {
      const components = componentQueue.current.map((props) => renderSingleField(props))
      componentQueue.current.length = 0
      return [<Grid key={`form-row-${idx}`}>{components}</Grid>, <Grid key={`form-${name}`}>{renderSingleField(kv)}</Grid>]
    } else {
      return <Grid key={`form-${name}`}>{renderSingleField(kv)}</Grid>
    }
  }

  useEffect(() => {
    onStateChange(isValid, errors)
  }, [isValid, errors, onStateChange])

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (!compareObjects(prevValues.current, value)) {
        // @ts-expect-error: OPERATION BLEED STOPPER
        prevValues.current = value
        onChange(value)
      }
    })

    return () => subscription.unsubscribe()
  }, [onChange, watch])

  return (
    // @ts-expect-error: OPERATION BLEED STOPPER
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit, onError)} onFocus={onFocus} data-testid={`${testNameSpace}-form`} noValidate>
        {transformedSchema.map(renderField)}
      </form>
    </FormProvider>
  )
}

export default forwardRef(FormBuilder)

export * from './types'
